// MediaCarousel.jsx - HYK-570: Simple carousel component for displaying multiple media files
import React, { useState, useCallback } from 'react';
import MediaPlayer from '../MediaPlayer/MediaPlayer';
import Icon from '../Icon/Icon';
import './MediaCarousel.scss';

const MediaCarousel = ({
  mediaArray = [], // Array of media objects: [{ media_url, media_type, media_format, ... }, ...]
  thumbnailUrl,
  isPreview = false,
  isNotFree = false,
  isPhysical = false,
  setOpenPurchaseAfterPreview = () => {},
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Normalize media array - handle both array and single object for backward compatibility
  const mediaItems = Array.isArray(mediaArray) 
    ? mediaArray 
    : mediaArray?.media_url 
      ? [mediaArray]
      : [];

  const totalItems = mediaItems.length;

  // If only one item or no items, render single MediaPlayer (backward compatible)
  if (totalItems <= 1) {
    const singleMedia = mediaItems[0] || mediaArray;
    if (!singleMedia?.media_url) {
      return null;
    }
    return (
      <div className="media-carousel media-carousel--single">
        <MediaPlayer
          mediaUrl={singleMedia.media_url}
          fileType={singleMedia.media_type}
          isPreview={isPreview}
          posterImage={thumbnailUrl}
          isPreviewfromViewCreation={true}
          setOpenPurchaseAfterPreview={setOpenPurchaseAfterPreview}
          isNotFree={isNotFree}
          isPhysical={singleMedia.media_format === 'physical' || isPhysical}
        />
      </div>
    );
  }

  // Navigation handlers
  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalItems);
  }, [totalItems]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalItems) % totalItems);
  }, [totalItems]);

  const handleGoToIndex = useCallback((index) => {
    if (index >= 0 && index < totalItems) {
      setCurrentIndex(index);
    }
  }, [totalItems]);

  const currentMedia = mediaItems[currentIndex];

  return (
    <div className="media-carousel">
      <div className="media-carousel__container">
        <div className="media-carousel__slide">
          {currentMedia && (
            <MediaPlayer
              key={`${currentMedia.media_id || currentIndex}-${currentMedia.media_url}`}
              mediaUrl={currentMedia.media_url}
              fileType={currentMedia.media_type}
              isPreview={isPreview}
              posterImage={thumbnailUrl}
              isPreviewfromViewCreation={true}
              setOpenPurchaseAfterPreview={setOpenPurchaseAfterPreview}
              isNotFree={isNotFree}
              isPhysical={currentMedia.media_format === 'physical' || isPhysical}
            />
          )}
        </div>
      </div>

      {/* Navigation Arrows */}
      {totalItems > 1 && (
        <>
          <button
            className="media-carousel__nav media-carousel__nav--prev"
            onClick={handlePrev}
            aria-label="Previous media"
          >
            <Icon alias="chevron-left" width={24} color="#FFFFFF" />
          </button>
          <button
            className="media-carousel__nav media-carousel__nav--next"
            onClick={handleNext}
            aria-label="Next media"
          >
            <Icon alias="chevron-right" width={24} color="#FFFFFF" />
          </button>
        </>
      )}

      {/* Dot Indicators */}
      {totalItems > 1 && (
        <div className="media-carousel__indicators">
          {mediaItems.map((_, index) => (
            <button
              key={index}
              className={`media-carousel__dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => handleGoToIndex(index)}
              aria-label={`Go to media ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Position Counter */}
      {totalItems > 1 && (
        <div className="media-carousel__counter">
          {currentIndex + 1} / {totalItems}
        </div>
      )}
    </div>
  );
};

export default MediaCarousel;

